/**
 * Desktop Shortcuts Configuration
 * Defines all available shortcuts that can be added to the admin desktop
 */

// Grid configuration for snap-to-grid feature
export const GRID_CONFIG = {
  GAP: 8, // Fixed gap between cells
  PADDING: 10, // Padding from canvas edges
  MIN_ICON_SIZE: 40, // Minimum icon size
  MAX_ICON_SIZE: 56, // Maximum icon size (prevents huge icons on PC)
  ICON_LABEL_HEIGHT: 20, // Height for label below icon

  // Fixed columns per device type
  PHONE_COLS: 6, // Phone always has 6 columns
  TABLET_COLS: 11, // Tablet always has 11 columns

  // Breakpoints for device detection
  PHONE_MAX_WIDTH: 499,
  TABLET_MAX_WIDTH: 1024,
}

/**
 * Get device type based on screen width
 * @param {number} width - Screen width
 * @returns {'phone' | 'tablet' | 'pc'}
 */
export function getDeviceType(width) {
  const { PHONE_MAX_WIDTH, TABLET_MAX_WIDTH } = GRID_CONFIG
  if (width <= PHONE_MAX_WIDTH) return 'phone'
  if (width <= TABLET_MAX_WIDTH) return 'tablet'
  return 'pc'
}

/**
 * Get maximum visible columns for a device type
 * @param {'phone' | 'tablet' | 'pc'} deviceType
 * @param {number} pcCols - Number of columns on PC (for pc device type)
 * @returns {number}
 */
export function getMaxColsForDevice(deviceType, pcCols = 20) {
  const { PHONE_COLS, TABLET_COLS } = GRID_CONFIG
  if (deviceType === 'phone') return PHONE_COLS
  if (deviceType === 'tablet') return TABLET_COLS
  return pcCols
}

/**
 * Calculate dynamic grid dimensions based on canvas size
 * @param {number} canvasWidth - Canvas width
 * @param {number} canvasHeight - Canvas height
 * @returns {{ cols: number, rows: number, cellWidth: number, cellHeight: number, iconSize: number, deviceType: string }}
 */
export function calculateGridDimensions(canvasWidth, canvasHeight) {
  const { GAP, PADDING, MIN_ICON_SIZE, MAX_ICON_SIZE, ICON_LABEL_HEIGHT, PHONE_COLS, TABLET_COLS } =
    GRID_CONFIG

  // Detect device type
  const deviceType = getDeviceType(canvasWidth)

  // Calculate available width (minus padding on both sides)
  const availableWidth = canvasWidth - PADDING * 2

  let cols

  if (deviceType === 'phone') {
    // Phone: fixed 6 columns
    cols = PHONE_COLS
  } else if (deviceType === 'tablet') {
    // Tablet: fixed 11 columns
    cols = TABLET_COLS
  } else {
    // PC: dynamic columns based on max icon size
    const targetCellWidth = MAX_ICON_SIZE + 10
    cols = Math.floor((availableWidth + GAP) / (targetCellWidth + GAP))
    cols = Math.max(TABLET_COLS + 1, cols) // At least more than tablet
  }

  // Calculate cell width for the number of columns
  let cellWidth = (availableWidth - GAP * (cols - 1)) / cols
  let iconSize = cellWidth - 10

  // Clamp icon size to valid range
  iconSize = Math.max(MIN_ICON_SIZE, Math.min(MAX_ICON_SIZE, iconSize))

  // Cell height = icon + label + some padding
  const cellHeight = iconSize + ICON_LABEL_HEIGHT + 15

  // Calculate rows based on available height
  const availableHeight = canvasHeight - PADDING * 2
  const rows = Math.max(2, Math.floor((availableHeight + GAP) / (cellHeight + GAP)))

  return { cols, rows, cellWidth, cellHeight, iconSize, deviceType }
}

/**
 * Calculate snap position for an icon
 * @param {number} x - Raw X position
 * @param {number} y - Raw Y position
 * @param {number} canvasWidth - Canvas width
 * @param {number} canvasHeight - Canvas height
 * @returns {{ x: number, y: number, col: number, row: number }}
 */
export function calculateSnapPosition(x, y, canvasWidth, canvasHeight) {
  const { GAP, PADDING } = GRID_CONFIG
  const { cols, rows, cellWidth, cellHeight } = calculateGridDimensions(canvasWidth, canvasHeight)

  // Calculate column (from right side for RTL)
  const fromRight = canvasWidth - x - PADDING
  const col = Math.max(0, Math.min(cols - 1, Math.round(fromRight / (cellWidth + GAP))))

  // Calculate row (from top)
  const fromTop = y - PADDING
  const row = Math.max(0, Math.min(rows - 1, Math.round(fromTop / (cellHeight + GAP))))

  return { col, row, ...getCellPosition(col, row, canvasWidth, canvasHeight) }
}

/**
 * Find nearest empty cell if target is occupied
 * @param {number} targetCol - Target column
 * @param {number} targetRow - Target row
 * @param {Set<string>} occupiedCells - Set of "col,row" strings
 * @param {number} maxCols - Maximum columns
 * @param {number} maxRows - Maximum rows
 * @returns {{ col: number, row: number }}
 */
export function findNearestEmptyCell(targetCol, targetRow, occupiedCells, maxCols, maxRows) {
  const cellKey = (c, r) => `${c},${r}`

  // If target is empty, use it
  if (!occupiedCells.has(cellKey(targetCol, targetRow))) {
    return { col: targetCol, row: targetRow }
  }

  // Search in expanding squares around target
  for (let distance = 1; distance < Math.max(maxCols, maxRows); distance++) {
    // Check cells at this distance
    for (let dc = -distance; dc <= distance; dc++) {
      for (let dr = -distance; dr <= distance; dr++) {
        // Only check cells at exactly this distance (perimeter)
        if (Math.abs(dc) !== distance && Math.abs(dr) !== distance) continue

        const newCol = targetCol + dc
        const newRow = targetRow + dr

        // Check bounds
        if (newCol < 0 || newCol >= maxCols || newRow < 0 || newRow >= maxRows) continue

        // Check if empty
        if (!occupiedCells.has(cellKey(newCol, newRow))) {
          return { col: newCol, row: newRow }
        }
      }
    }
  }

  // Fallback: return target anyway (will overlap)
  return { col: targetCol, row: targetRow }
}

/**
 * Get cell position from col/row
 * @param {number} col - Column index (0 = rightmost for RTL)
 * @param {number} row - Row index (0 = top)
 * @param {number} canvasWidth - Canvas width
 * @param {number} canvasHeight - Canvas height
 * @returns {{ x: number, y: number }}
 */
export function getCellPosition(col, row, canvasWidth, canvasHeight = 500) {
  const { GAP, PADDING } = GRID_CONFIG
  const { cellWidth, cellHeight } = calculateGridDimensions(canvasWidth, canvasHeight)

  // Position from right edge (RTL)
  // Col 0 = rightmost position
  const x = canvasWidth - PADDING - cellWidth - col * (cellWidth + GAP)
  const y = PADDING + row * (cellHeight + GAP)

  return { x, y }
}

// All available shortcuts organized by category
export const AVAILABLE_SHORTCUTS = {
  // ===== Admin Sidebar Menu Items =====
  sidebar: {
    label: 'תפריט ניהול',
    items: [
      {
        id: 'admin-dashboard',
        routeName: 'admin-dashboard',
        label: 'שולחן עבודה',
        icon: 'dashboard',
        roles: ['admin'],
      },
      {
        id: 'catalog',
        routeName: 'catalog',
        label: 'קטלוג',
        icon: 'catalog',
        roles: ['admin', 'agent', 'user'],
      },
      {
        id: 'profile',
        routeName: 'profile',
        label: 'פרופיל',
        icon: 'profile',
        roles: ['admin', 'agent', 'user'],
      },
      {
        id: 'my-orders',
        routeName: 'my-orders',
        label: 'ההזמנות שלי',
        icon: 'my-orders',
        roles: ['admin', 'user'],
      },
      {
        id: 'admin-orders',
        routeName: 'admin-orders',
        label: 'ניהול הזמנות',
        icon: 'orders',
        roles: ['admin'],
      },
      {
        id: 'admin-catalog',
        routeName: 'admin-catalog',
        label: 'ניהול פריטים',
        icon: 'items',
        roles: ['admin'],
      },
      {
        id: 'admin-departments',
        routeName: 'admin-departments',
        label: 'מחלקות',
        icon: 'departments',
        roles: ['admin'],
      },
      {
        id: 'admin-promotions',
        routeName: 'admin-promotions',
        label: 'ניהול מבצעים',
        icon: 'promotions',
        roles: ['admin'],
      },
      {
        id: 'admin-whatsapp',
        routeName: 'admin-whatsapp',
        label: 'WhatsApp',
        icon: 'whatsapp',
        roles: ['admin'],
      },
      {
        id: 'admin-data',
        routeName: 'admin-data',
        label: 'נתונים',
        icon: 'data',
        roles: ['admin'],
      },
      {
        id: 'admin-users',
        routeName: 'admin-users',
        label: 'ניהול משתמשים',
        icon: 'users',
        roles: ['admin'],
      },
      {
        id: 'admin-clients',
        routeName: 'admin-clients',
        label: 'ניהול לקוחות',
        icon: 'clients',
        roles: ['admin'],
      },
      {
        id: 'admin-levels',
        routeName: 'admin-levels',
        label: 'ניקוד ורמות',
        icon: 'levels',
        roles: ['admin'],
      },
      {
        id: 'admin-contact-forms',
        routeName: 'admin-contact-forms',
        label: 'טפסי יצירת קשר',
        icon: 'contact-forms',
        roles: ['admin'],
      },
      {
        id: 'admin-b2b-forms',
        routeName: 'admin-b2b-forms',
        label: 'ניהול לידים',
        icon: 'b2b-leads',
        roles: ['admin'],
      },
      {
        id: 'admin-b2b-divur',
        routeName: 'admin-b2b-divur',
        label: 'דיוור',
        icon: 'b2b-divur',
        roles: ['admin'],
      },
      {
        id: 'admin-campaign-management',
        routeName: 'admin-campaign-management',
        label: 'ניהול קמפיינים',
        icon: 'campaign',
        roles: ['admin'],
      },
      {
        id: 'admin-commits',
        routeName: 'admin-commits',
        label: 'קומיטים',
        icon: 'commits',
        roles: ['admin'],
      },
      {
        id: 'admin-agent-requests',
        routeName: 'admin-agent-requests',
        label: 'ניהול בקשות חופשה',
        icon: 'vacation-request',
        roles: ['admin'],
      },
      {
        id: 'admin-emails',
        routeName: 'admin-emails',
        label: 'אימיילים',
        icon: 'emails',
        roles: ['admin'],
      },
      {
        id: 'admin-mailing',
        routeName: 'admin-mailing',
        label: 'ניהול מערכת דיוור',
        icon: 'mailing',
        roles: ['admin'],
      },
      {
        id: 'admin-sales-analysis',
        routeName: 'admin-sales-analysis',
        label: 'ביצועי מכירות',
        icon: 'sales-analysis',
        roles: ['admin'],
      },
      {
        id: 'admin-attendance',
        routeName: 'admin-attendance',
        label: 'ניהול נוכחות',
        icon: 'attendance',
        roles: ['admin'],
      },
      {
        id: 'admin-salary-wizard',
        routeName: 'admin-salary-wizard',
        label: 'אשף שכר',
        icon: 'salary-wizard',
        roles: ['admin'],
      },
      {
        id: 'admin-devblog',
        routeName: 'admin-devblog',
        label: 'יומן הפיתוח',
        icon: 'devblog',
        roles: ['admin'],
      },
      {
        id: 'admin-signage-select',
        routeName: 'admin-signage-select',
        label: 'מערכת שילוט',
        icon: 'signage',
        roles: ['admin'],
      },
    ],
  },

  // ===== Agents Portal Items =====
  agentsPortal: {
    label: 'פורטל סוכנים',
    items: [
      {
        id: 'agents-portal',
        routeName: 'agents-portal',
        label: 'פורטל סוכנים',
        icon: 'portal',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-clients',
        routeName: 'agents-clients',
        label: 'לקוחות',
        icon: 'users-group',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-create-client',
        routeName: 'agents-create-client',
        label: 'יצירת לקוח חדש',
        icon: 'user-plus',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-update-client',
        routeName: 'agents-update-client',
        label: 'עדכון פרטי לקוח',
        icon: 'user-edit',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-gvia',
        routeName: 'agents-gvia',
        label: 'גבייה',
        icon: 'credit-card',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-goals',
        routeName: 'agents-goals',
        label: 'יעדים',
        icon: 'target',
        roles: ['admin'],
      },
      {
        id: 'agents-holidays',
        routeName: 'agents-holidays',
        label: 'חגים',
        icon: 'calendar',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-statistics',
        routeName: 'agents-statistics',
        label: 'סטטיסטיקות',
        icon: 'chart',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-requests',
        routeName: 'agents-requests',
        label: 'בקשות חופשה',
        icon: 'vacation-request',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-app-transfer',
        routeName: 'agents-app-transfer',
        label: 'העברה לאפליקציה',
        icon: 'app-transfer',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-attendance',
        routeName: 'agents-attendance',
        label: 'נוכחות',
        icon: 'attendance',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-wallet',
        routeName: 'agents-wallet',
        label: 'קופה',
        icon: 'wallet',
        roles: ['admin', 'agent'],
      },
      {
        id: 'agents-wallet-packages',
        routeName: 'agents-wallet-packages',
        label: 'פריקת חבילות',
        icon: 'walletPackages',
        roles: ['admin'],
      },
    ],
  },
}

// Icon SVG paths map - used for rendering icons
export const ICON_SVG_PATHS = {
  dashboard: 'M3 3h7v7H3V3zm11 0h7v7h-7V3zm0 11h7v7h-7v-7zm-11 0h7v7H3v-7z',
  catalog:
    'M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z',
  profile: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 100 8 4 4 0 000-8z',
  'my-orders':
    'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11zm-3-5v-1l-1-1H8l-1 1v1l1 1h6l1-1zm0-4v-1l-1-1H8l-1 1v1l1 1h6l1-1z',
  orders:
    'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z',
  items: 'M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z',
  departments:
    'M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm-1 6h2v2h-2V8zm0 4h2v6h-2v-6z',
  promotions:
    'M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z',
  whatsapp:
    'M12 1.95C6.48 1.95 2 6.43 2 11.95c0 1.89.52 3.66 1.43 5.17L2 22l5.07-1.4A9.96 9.96 0 0 0 12 21.95c5.52 0 10-4.48 10-10s-4.48-10-10-10zm5.16 14.12c-.22.62-1.28 1.15-1.76 1.22-.45.06-1.01.09-1.63-.1-.38-.12-.86-.28-1.48-.55-2.6-1.12-4.3-3.75-4.43-3.92-.13-.18-1.08-1.44-1.08-2.74s.68-1.94.93-2.21c.24-.26.52-.33.7-.33l.5.01c.16 0 .38-.06.6.46l.86 2.09c.07.17.12.37.01.58l-.32.46-.45.52c-.1.1-.2.21-.09.41.12.2.52.85 1.12 1.38.77.68 1.42.9 1.62.99.2.1.32.08.44-.05l.5-.59c.14-.17.27-.12.46-.05l2.01.95c.19.09.31.13.35.21.05.07.05.44-.17 1.06z',
  data: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z',
  users:
    'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  clients:
    'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
  levels: 'M7.5 21H2V9h5.5v12zm7.25-18h-5.5v18h5.5V3zM22 11h-5.5v10H22V11z',
  portal: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
  'users-group':
    'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  'user-plus':
    'M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
  'user-edit':
    'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-1.5 9.5l1.5-1.5v-2l-3 3h1.5zm7.5-6l-1.06 1.06L15.88 15.5l1.06-1.06c.2-.2.51-.2.71 0l.35.35c.2.2.2.51 0 .71zM4 20v-2c0-2.66 5.33-4 8-4 .37 0 .8.03 1.25.08L11 16.33V20H4z',
  'credit-card':
    'M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z',
  target:
    'M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3-8c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3z',
  calendar:
    'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z',
  chart: 'M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zm5.6 8H19v6h-2.8v-6z',
  'vacation-request': 'M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z',
  'contact-forms':
    'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  'b2b-leads':
    'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  campaign:
    'M2 9v6c0 .55.45 1 1 1h2v3h2v-3l11 4V5L7 9H3c-.55 0-1 .45-1 1zm14-2.61v11.22L7 14.61V9.39l9-3z',
  commits:
    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z M9 12 M15 12 M12 9 M12 15',
  'sales-analysis':
    'M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zm5.6 8H19v6h-2.8v-6zM4 14l4-4 4 2 8-8M16 4h4v4',
  'app-transfer':
    'M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14zm-4-6h-2V9h-2l3-3 3 3h-2z',
  attendance:
    'M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z',
  'salary-wizard':
    'M1 8a2 2 0 012-2h18a2 2 0 012 2v9a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm11 7.5a3 3 0 100-6 3 3 0 000 6zM5 9v.01M19 16v.01',
  emails:
    'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  mailing:
    'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zm18 2l-10 7L2 6',
  'b2b-divur': 'M3 11l18-8-8 18-2-8-8-2z',
  wallet:
    'M20 6H4c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H4v-4h16v4zm0-6H4V8h16v2zm-2 4h-2v-2h2v2z',
  walletPackages:
    'M1 3h15a2 2 0 012 2v8a2 2 0 01-2 2H1zm15 5h4l3 3v5h-3m-14 0a2 2 0 104 0m9 0a2 2 0 104 0',
  signage: 'M2 3h20a2 2 0 012 2v10a2 2 0 01-2 2H2a2 2 0 01-2-2V5a2 2 0 012-2zm6 18h8m-4-4v4',
  devblog:
    'M20 3H4a1 1 0 00-1 1v16l3-2 3 2 3-2 3 2 3-2 3 2V4a1 1 0 00-1-1zm-9 12H6v-2h5v2zm0-4H6V9h5v2zm0-4H6V5h5v2zm7 8h-4v-2h4v2zm0-4h-4V9h4v2zm0-4h-4V5h4v2z',
}

/**
 * Filter available shortcuts by user role
 * @param {string} role - User role ('admin', 'agent', 'user')
 * @returns {Object} Filtered shortcuts by category
 */
export function getAvailableShortcutsForRole(role) {
  const result = {}

  for (const [category, data] of Object.entries(AVAILABLE_SHORTCUTS)) {
    const filteredItems = data.items.filter((item) => item.roles.includes(role))
    if (filteredItems.length > 0) {
      result[category] = {
        label: data.label,
        items: filteredItems,
      }
    }
  }

  return result
}

/**
 * Get a flat list of all shortcuts
 * @returns {Array} All shortcuts as flat array
 */
export function getAllShortcuts() {
  const all = []
  for (const category of Object.values(AVAILABLE_SHORTCUTS)) {
    all.push(...category.items)
  }
  return all
}

/**
 * Find a shortcut by its ID
 * @param {string} id - Shortcut ID
 * @returns {Object|null} Shortcut config or null
 */
export function getShortcutById(id) {
  for (const category of Object.values(AVAILABLE_SHORTCUTS)) {
    const found = category.items.find((item) => item.id === id)
    if (found) return found
  }
  return null
}
